/**
 * O editor de texto livre — o "Docs" do site.
 *
 * É o mesmo TinyMCE auto-hospedado do editor acadêmico, com uma diferença de
 * propósito: **aqui a formatação é livre**. No documento da ABNT a barra é curta
 * porque o papel manda (fonte, corpo e alinhamento são da norma); aqui vale o que
 * a pessoa escolher — fonte, tamanho, cor, alinhamento, link, imagem, tabela.
 *
 * O que não muda: nenhum HTML do editor é executado como código. O que ele produz
 * é limpo no servidor (`limparHtml`), e a imagem que entra é enviada pelo mesmo
 * caminho de `/arquivos` que o resto do site usa.
 *
 * Pensado para gente de todas as idades: a barra tem rótulo em português, alvos
 * grandes no celular, e o botão de apresentação fica na tela de fora deste
 * componente (é para **ler**, não para escrever).
 */

import { useEffect, useImperativeHandle, useRef } from 'react';
import type { Ref } from 'react';
import type { FonteDoTexto, FormatoDoTexto } from '@miolos/core';
import { FAMILIA_DA_FONTE, LARGURA_DA_TELA } from '@miolos/core';
import { api } from '@/lib/api';

const BASE = '/tinymce';
const CHAVE = (import.meta.env.VITE_TINYMCE_LICENSE_KEY as string | undefined) ?? 'gpl';

/**
 * Em que elementos o alinhamento age.
 *
 * O parágrafo, os títulos, o item de lista e a célula de tabela: são os blocos que
 * a pessoa alinha na prática. Deixar de fora o `li` faria o botão não fazer nada
 * dentro de uma lista, que é onde ele mais é usado.
 */
const SELETOR_DE_BLOCO = 'p,h1,h2,h3,h4,h5,h6,li,td,th,div,blockquote';

/** O subconjunto do editor que este componente usa. */
interface EventoDoEditor {
  ctrlKey?: boolean;
  metaKey?: boolean;
  key?: string;
  preventDefault?: () => void;
}

interface EditorTiny {
  getContent: () => string;
  setContent: (html: string) => void;
  insertContent: (html: string) => void;
  focus: () => void;
  remove: () => void;
  on: (evento: string, retorno: (evento?: EventoDoEditor) => void) => void;
  getBody: () => HTMLElement | null;
  mode: { set: (modo: string) => void };
}

/** O que a tela pode pedir ao editor (a Bíblia usa o `inserirHtml`). */
export interface EditorLivreHandle {
  /** Cola o HTML onde está o cursor. */
  inserirHtml: (html: string) => void;
  /** Devolve o que está escrito agora (sem esperar o evento de mudança). */
  conteudoAtual: () => string;
  /** Leva o cursor para o texto. */
  focar: () => void;
}

/**
 * Onde a coluna de texto para.
 *
 * `LARGURA_DA_TELA` (0) quer dizer **tela toda**: o editor usa 100% da largura, e
 * quem escolhe isso é quem cola tabela larga ou texto vindo de fora.
 */
function limiteDaColuna(formato: FormatoDoTexto): string {
  return formato.larguraMm === LARGURA_DA_TELA ? 'none' : `${formato.larguraMm}mm`;
}

let carregando: Promise<void> | null = null;

function carregarTiny(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  const janela = window as unknown as { tinymce?: unknown };
  if (janela.tinymce) return Promise.resolve();
  if (carregando) return carregando;

  carregando = new Promise<void>((resolve, rejeitar) => {
    const script = document.createElement('script');
    script.src = `${BASE}/tinymce.min.js`;
    script.referrerPolicy = 'origin';
    script.onload = () => resolve();
    script.onerror = () => rejeitar(new Error('Não foi possível carregar o editor de texto.'));
    document.head.appendChild(script);
  });
  return carregando;
}

/**
 * O CSS de dentro do editor: a fonte e a coluna escolhidas.
 *
 * **A coluna é feita no conteúdo, não na caixa.** A tentação é estreitar a caixa
 * do editor em `larguraMm`, mas aí a barra de ferramentas — que vive na mesma
 * caixa — fica com ~570 px e o TinyMCE esconde metade dos botões atrás de um "…".
 * Foi assim que o alinhamento desapareceu da tela. Aqui a caixa ocupa a largura
 * toda e cada bloco de texto é centralizado na medida da coluna.
 */
export function estiloDoTexto(formato: FormatoDoTexto, corpoPt = formato.corpoPt): string {
  return `
    body {
      font-family: ${FAMILIA_DA_FONTE[formato.fonte]};
      font-size: ${corpoPt}pt;
      line-height: 1.6;
      color: #1a1a1a;
      margin: 0;
      padding: 24px 16px;
    }
    body > * {
      max-width: ${limiteDaColuna(formato)};
      margin-left: auto;
      margin-right: auto;
    }
    p { margin: 0 0 0.8em; }
    h1 { font-size: 1.8em; margin: 1em 0 0.5em; }
    h2 { font-size: 1.45em; margin: 1em 0 0.5em; }
    h3 { font-size: 1.2em; margin: 0.9em 0 0.4em; }
    blockquote { margin: 0 auto 1em; padding-left: 1em; border-left: 3px solid #d4d4d4; color: #444; }
    table { border-collapse: collapse; margin-bottom: 1em; }
    th, td { border: 1px solid #bbb; padding: 6px 8px; }
    img { max-width: 100%; height: auto; }
    ul, ol { margin: 0 auto 0.8em; }
  `;
}

interface Props {
  /** O HTML que veio do servidor. */
  html: string;
  formato: FormatoDoTexto;
  /** Quando muda, o conteúdo é reinjetado (abrir outro texto, desfazer de fora). */
  versao: number;
  aoMudar: (html: string) => void;
  /** Ctrl+S dentro do editor. */
  aoSalvar?: () => void;
  altura?: number;
  /** Texto compartilhado só para leitura: a barra some e o texto não aceita tecla. */
  somenteLeitura?: boolean;
  ref?: Ref<EditorLivreHandle>;
}

export function EditorLivre({
  html,
  formato,
  versao,
  aoMudar,
  aoSalvar,
  altura = 560,
  somenteLeitura = false,
  ref,
}: Props) {
  const caixa = useRef<HTMLDivElement | null>(null);
  const editor = useRef<EditorTiny | null>(null);
  const pronto = useRef(false);
  const ultimo = useRef<string>('');
  const aoMudarAtual = useRef(aoMudar);
  const aoSalvarAtual = useRef(aoSalvar);

  aoMudarAtual.current = aoMudar;
  aoSalvarAtual.current = aoSalvar;

  /* A tela precisa poder colar HTML no cursor (é o que a Bíblia faz). */
  useImperativeHandle(
    ref,
    () => ({
      inserirHtml: (bloco: string) => {
        const atual = editor.current;
        if (!atual) return;
        atual.insertContent(bloco);
        atual.focus();
      },
      conteudoAtual: () => editor.current?.getContent() ?? '',
      focar: () => editor.current?.focus(),
    }),
    [],
  );

  /* Monta o editor uma vez. */
  useEffect(() => {
    let vivo = true;

    void carregarTiny()
      .then(() => {
        if (!vivo || !caixa.current) return;
        const tiny = (window as unknown as {
          tinymce: { init: (config: Record<string, unknown>) => Promise<EditorTiny> };
        }).tinymce;

        return tiny.init({
          target: caixa.current,
          base_url: BASE,
          suffix: '.min',
          license_key: CHAVE,
          menubar: false,
          branding: false,
          promotion: false,
          statusbar: true,
          height: altura,
          resize: true,
          readonly: somenteLeitura,
          /*
           * O resto dos recursos do Word e do Docs que o pacote livre do TinyMCE
           * oferece: marcas de parágrafo (visualblocks), caracteres especiais
           * (charmap), data e hora (insertdatetime), quebra de página (pagebreak),
           * espaço que não quebra (nonbreaking), tela cheia, prévia e barra rápida
           * ao selecionar um trecho (quickbars) — esta última é o que mais se
           * parece com o Docs: seleciona, aparece a barra em cima.
           */
          plugins:
            'lists advlist table link image code searchreplace wordcount autolink hr charmap insertdatetime pagebreak nonbreaking visualblocks fullscreen preview quickbars anchor directionality',
          browser_spellcheck: true,
          /*
           * A barra é a de um processador de texto comum — é o que a pessoa
           * reconhece. Os rótulos do formato de bloco ficam em português.
           *
           * São **duas linhas** e `toolbar_mode: 'wrap'` de propósito: numa linha
           * só, o TinyMCE escondia o que não coubesse atrás de um "…" — e era
           * justamente o alinhamento que sumia na tela estreita. Botão que existe e
           * não aparece é botão que não existe.
           */
          toolbar: [
            'undo redo | blocks | fontfamily fontsize lineheight | bold italic underline strikethrough | forecolor backcolor | removeformat',
            'alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image table hr | charmap insertdatetime pagebreak nonbreaking | searchreplace code preview fullscreen visualblocks',
          ],
          toolbar_mode: 'wrap',
          /*
           * A barra que aparece ao selecionar um trecho — o gesto do Docs e do
           * Word. Aqui ela só traz o que se usa com a mão no texto.
           */
          quickbars_selection_toolbar: 'bold italic underline strikethrough | h2 h3 blockquote | alignleft aligncenter alignright | quicklink',
          quickbars_insert_toolbar: 'quicktable hr',
          quickbars_image_toolbar: 'alignleft aligncenter alignright | image',
          lineheight_formats: '1 1.15 1.5 1.75 2',
          /*
           * O alinhamento é gravado como `style="text-align: ..."` no próprio
           * bloco. Isso importa para as outras duas saídas: o `.docx` lê esse
           * `style` (`docxDoTexto.ts`) e a impressão obedece ao CSS inline. Se o
           * editor usasse uma classe, o PDF e o Word sairiam alinhados à esquerda.
           */
          formats: {
            alignleft: { selector: SELETOR_DE_BLOCO, styles: { 'text-align': 'left' } },
            aligncenter: { selector: SELETOR_DE_BLOCO, styles: { 'text-align': 'center' } },
            alignright: { selector: SELETOR_DE_BLOCO, styles: { 'text-align': 'right' } },
            alignjustify: { selector: SELETOR_DE_BLOCO, styles: { 'text-align': 'justify' } },
          },
          block_formats: 'Parágrafo=p; Título=h1; Subtítulo=h2; Seção=h3; Texto menor=p',
          font_size_formats: '10pt 12pt 14pt 16pt 18pt 24pt 30pt',
          content_css: false,
          entity_encoding: 'raw',
          relative_urls: false,
          convert_urls: false,
          remove_script_host: false,
          paste_as_text: false,
          /*
           * Imagem colada ou escolhida sobe pelo mesmo caminho de `/arquivos` do
           * resto do site: nada de base64 gigante dentro do HTML, que estouraria o
           * limite do texto e não abriria no `.docx`.
           */
          images_upload_handler: async (blobInfo: { blob: () => Blob; filename: () => string }) => {
            const arquivo = new File([blobInfo.blob()], blobInfo.filename() || 'imagem.png', {
              type: blobInfo.blob().type,
            });
            const { arquivo: enviado } = await api.enviarArquivo(arquivo);
            return enviado.url;
          },
          setup: (instancia: EditorTiny) => {
            editor.current = instancia;
            instancia.on('init', () => {
              const documento = instancia.getBody()?.ownerDocument;
              if (documento) {
                const folha = documento.createElement('style');
                folha.id = 'estilo-do-texto-livre';
                folha.textContent = estiloDoTexto(formato);
                documento.head.appendChild(folha);
              }
              instancia.setContent(html);
              ultimo.current = html;
              pronto.current = true;
            });
            instancia.on('input change undo redo', () => {
              const conteudo = instancia.getContent();
              ultimo.current = conteudo;
              aoMudarAtual.current(conteudo);
            });
            /*
             * `Ctrl+S` dentro do texto também salva: com o cursor no editor, o
             * teclado está dentro do `iframe` e o evento não sobe para a página.
             */
            instancia.on('keydown', (evento) => {
              if ((evento?.ctrlKey || evento?.metaKey) && evento?.key?.toLowerCase() === 's') {
                evento.preventDefault?.();
                aoSalvarAtual.current?.();
              }
            });
          },
        });
      })
      .catch(() => undefined);

    return () => {
      vivo = false;
      editor.current?.remove();
      editor.current = null;
      pronto.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Conteúdo novo vindo de fora: reinjeta sem atropelar quem está digitando. */
  useEffect(() => {
    if (!pronto.current) return;
    const atual = editor.current;
    if (!atual) return;
    if (html === ultimo.current) return;
    atual.setContent(html);
    ultimo.current = html;
  }, [versao, html]);

  /* A fonte ou o corpo mudou: troca só o CSS de dentro do editor. */
  useEffect(() => {
    if (!pronto.current) return;
    const corpo = editor.current?.getBody();
    const documento = corpo?.ownerDocument;
    const folha = documento?.getElementById('estilo-do-texto-livre');
    if (folha) folha.textContent = estiloDoTexto(formato);
  }, [formato]);

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-white">
      <div ref={caixa} />
    </div>
  );
}

/** A família CSS de uma fonte, para as telas que desenham o texto fora do editor. */
export function familiaDaFonteDoTexto(fonte: FonteDoTexto): string {
  return FAMILIA_DA_FONTE[fonte];
}
